import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Circle, ListChecks, Sparkles, Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const METHODS = [
  {
    id: "record",
    icon: Circle,
    title: "Record & Playback",
    desc: "Interact with your app in a live browser. Every click and keystroke becomes a step.",
  },
  {
    id: "manual",
    icon: ListChecks,
    title: "Build steps manually",
    desc: "Compose steps one by one in the no-code builder.",
  },
  {
    id: "ai",
    icon: Sparkles,
    title: "Generate with AI",
    desc: "Describe the test in plain English. AI drafts the steps for your review.",
  },
];

export const NewTestDialog = ({ open, onOpenChange, projectId, suites = [], environments = [], defaultSuiteId, defaultEnvironmentId, defaultTargetUrl }) => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const computedDefaultSuite = defaultSuiteId || (suites[0]?.id ?? "");
  const [suiteId, setSuiteId] = useState(computedDefaultSuite);
  const computedDefaultEnv = defaultEnvironmentId || (environments[0]?.id ?? "");
  const [envId, setEnvId] = useState(computedDefaultEnv);
  // URL state: lazily derived from selected environment unless the user
  // explicitly types a value (urlDirty=true). This means most users never
  // have to touch the field — the env's base_url is the source of truth.
  const selectedEnv = environments.find((e) => e.id === envId);
  const envBaseUrl = (selectedEnv?.base_url || "").trim();
  const [urlDirty, setUrlDirty] = useState(false);
  const [urlOverride, setUrlOverride] = useState(defaultTargetUrl || "");
  const url = urlDirty ? urlOverride : (envBaseUrl || defaultTargetUrl || "");
  const [method, setMethod] = useState("record");
  const [authStateId, setAuthStateId] = useState("none");
  const [aiDescription, setAiDescription] = useState("");
  const [authStates, setAuthStates] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/auth-states").then((r) => setAuthStates(r.data)).catch(() => {});
  }, [open]);

  // Reset URL override whenever the environment changes — env switch should
  // pick up the new base_url, not stick on the previous override.
  useEffect(() => {
    setUrlDirty(false);
    setUrlOverride("");
  }, [envId]);

  const handleCreate = async () => {
    const effective = (url || "").trim();
    if (!name.trim()) {
      toast.error("Test name is required");
      return;
    }
    if (!effective) {
      toast.error("Pick an environment with a Base URL, or enter a target URL.");
      return;
    }
    const targetUrl = effective.startsWith("http") ? effective : `https://${effective}`;
    const stateId = authStateId === "none" ? null : authStateId;

    if (method === "record") {
      const qs = new URLSearchParams({ name: name.trim(), url: targetUrl });
      if (stateId) qs.set("authStateId", stateId);
      if (suiteId) qs.set("suiteId", suiteId);
      if (projectId) qs.set("projectId", projectId);
      if (envId) qs.set("envId", envId);
      navigate(`/record?${qs.toString()}`);
      return;
    }

    setSubmitting(true);
    try {
      let steps = [
        { action: "goto", url: targetUrl, description: `Navigate to ${targetUrl}` },
      ];
      if (method === "ai") {
        if (aiDescription.trim().length < 5) {
          toast.error("Describe the test in a few words first");
          setSubmitting(false);
          return;
        }
        const { data } = await api.post("/ai/generate-steps", {
          target_url: targetUrl,
          description: aiDescription.trim(),
        });
        steps = data.steps;
      }
      const { data: test } = await api.post("/tests", {
        name: name.trim(),
        target_url: targetUrl,
        auth_state_id: stateId,
        project_id: projectId || null,
        suite_id: suiteId || null,
        default_environment_id: envId || null,
        steps,
      });
      toast.success(method === "ai" ? "AI draft ready — review the steps" : "Test created");
      navigate(`/tests/${test.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const submitLabel = method === "record" ? "Start recording" : method === "ai" ? "Generate & review" : "Create test";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] rounded-xl border border-[#E5E7EB] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#F1F5F9]">
          <DialogTitle className="font-['Geist'] font-semibold text-xl tracking-tight text-[#0F172A]">
            New test case
          </DialogTitle>
          <DialogDescription className="text-sm text-[#64748B]">
            Choose how to create your test: record it live, build steps manually, or generate with AI.
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Step 1 — Pick the environment + suite first so the rest of the
              form can derive sane defaults (target URL, etc.) from them. */}
          {environments.length > 0 && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-[#475569]">Environment</label>
              <Select value={envId} onValueChange={setEnvId}>
                <SelectTrigger className="rounded-md border-[#E5E7EB] focus:ring-[#3B82F6]" data-testid="new-test-environment-select">
                  <SelectValue placeholder="Select an environment" />
                </SelectTrigger>
                <SelectContent className="rounded-md border-[#E5E7EB]">
                  {environments.map((e) => (
                    <SelectItem key={e.id} value={e.id} data-testid={`new-test-environment-option-${e.id}`}>
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: e.type_color || "#64748B" }} />
                        <span>{e.name}</span>
                        <span className="text-[10px] uppercase tracking-wide text-[#94A3B8]">{e.type}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {envBaseUrl ? (
                <p className="text-[11px] text-[#94A3B8]">
                  Tests will run against{" "}
                  <span className="font-mono text-[#475569]">{envBaseUrl}</span> by default.
                </p>
              ) : (
                <p className="text-[11px] text-[#B45309]">
                  This environment has no Base URL set — enter a target URL below.
                </p>
              )}
            </div>
          )}

          {suites.length > 0 && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-[#475569]">Suite</label>
              <Select value={suiteId} onValueChange={setSuiteId}>
                <SelectTrigger className="rounded-md border-[#E5E7EB] focus:ring-[#3B82F6]" data-testid="new-test-suite-select">
                  <SelectValue placeholder="Select a suite" />
                </SelectTrigger>
                <SelectContent className="rounded-md border-[#E5E7EB]">
                  {suites.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`new-test-suite-option-${s.id}`}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Step 2 — Identify the test */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-[#475569]">Test name</label>
            <Input
              data-testid="new-test-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Login flow works"
              className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6] focus-visible:border-[#3B82F6]"
            />
          </div>

          {/* Step 3 — Optional override of the env's base URL. Most users
              won't touch this; it's surfaced only when no env base_url
              exists OR the user clicks "Override". */}
          {(!envBaseUrl || urlDirty) ? (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-[#475569]">
                Target URL {envBaseUrl && <span className="text-[#94A3B8] font-normal">(override)</span>}
              </label>
              <Input
                data-testid="new-test-url-input"
                value={urlDirty ? urlOverride : envBaseUrl}
                onChange={(e) => { setUrlDirty(true); setUrlOverride(e.target.value); }}
                placeholder="https://your-app.com"
                className="rounded-md border-[#E5E7EB] font-mono text-xs focus-visible:ring-[#3B82F6] focus-visible:border-[#3B82F6]"
              />
              {urlDirty && envBaseUrl && (
                <button
                  type="button"
                  onClick={() => { setUrlDirty(false); setUrlOverride(""); }}
                  data-testid="new-test-url-reset"
                  className="text-[11px] text-[#3B82F6] hover:underline"
                >
                  Reset to environment Base URL
                </button>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => { setUrlDirty(true); setUrlOverride(envBaseUrl); }}
              data-testid="new-test-url-override"
              className="text-[11px] text-[#3B82F6] hover:underline"
            >
              Override target URL for this test
            </button>
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-[#475569]">Saved login state (optional)</label>
            <Select value={authStateId} onValueChange={setAuthStateId}>
              <SelectTrigger className="rounded-md border-[#E5E7EB] focus:ring-[#3B82F6]" data-testid="new-test-auth-state-select">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent className="rounded-md border-[#E5E7EB]">
                <SelectItem value="none">None — start logged out</SelectItem>
                {authStates.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-[#475569]">Creation method</label>
            <div className="grid gap-2">
              {METHODS.map((m) => {
                const Icon = m.icon;
                const active = method === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    data-testid={`method-${m.id}-option`}
                    onClick={() => setMethod(m.id)}
                    className={`flex items-start gap-3 text-left p-3.5 rounded-lg border transition-colors duration-150 ${
                      active
                        ? "border-[#3B82F6] bg-[#3B82F6]/[0.04] ring-1 ring-[#3B82F6]"
                        : "border-[#E5E7EB] bg-white hover:border-[#CBD5E1] hover:bg-[#F8FAFC]"
                    }`}
                  >
                    <Icon size={16} className={`mt-0.5 ${active ? "text-[#3B82F6]" : "text-[#64748B]"}`} />
                    <div>
                      <div className={`text-sm font-semibold ${active ? "text-[#0F172A]" : "text-[#0F172A]"}`}>{m.title}</div>
                      <div className="text-xs text-[#64748B] mt-0.5 leading-relaxed">{m.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {method === "ai" && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-[#475569]">Describe the test in English</label>
              <Textarea
                data-testid="ai-description-textarea"
                value={aiDescription}
                onChange={(e) => setAiDescription(e.target.value)}
                placeholder={'e.g. "Click the Sign In button, fill the email and password fields, submit the form, and verify the dashboard heading appears"'}
                rows={4}
                className="rounded-md border-[#E5E7EB] text-sm focus-visible:ring-[#3B82F6] focus-visible:border-[#3B82F6]"
              />
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-[#F1F5F9] flex justify-end gap-2 bg-[#F8FAFC]">
          <button
            onClick={() => onOpenChange(false)}
            data-testid="new-test-cancel-button"
            className="px-4 h-9 text-sm font-medium border border-[#E5E7EB] rounded-md bg-white text-[#0F172A] hover:bg-[#F1F5F9] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={submitting}
            data-testid="new-test-submit-button"
            className="px-4 h-9 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] transition-colors disabled:opacity-60 flex items-center gap-1.5"
          >
            {submitting ? <Loader2 size={13} className="animate-spin" /> : (method === "record" ? <Play size={13} /> : null)}
            {submitLabel}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
